import type { FormEvent, ReactNode } from 'react';
import type { AgentTokenExpiryDays } from 'definitions/agent-tokens';
import type { AgentTokenWebsiteOption } from '@/state/useAgentTokens';
import type { AgentAccessManager, WebsiteMode } from './use-agent-access';
import { Link } from 'react-router';
import { Info } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/Dialogs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FIELD_CONTROL } from '../../ConsolePanel';
import { LoadFailed } from './LoadFailed';
import { WebsiteRow, WebsiteSelectAll } from './WebsiteSelectAll';
import { AGENT_ACCESS_COPY, SCOPE_COPY } from './agent-access-copy';

type ManagerProps = { manager: AgentAccessManager };

const COPY = AGENT_ACCESS_COPY;

// ConfirmDialog's shell, class for class, as the create flag dialog uses it: the header strip, a
// body that scrolls, and a footer that stays. Written out because this footer carries more than
// ConfirmDialog's can (the passkey notice and the create's error, beside the actions), and flex
// rather than the primitive's grid so a short viewport shrinks the body and never the actions.
const SHELL = 'bg-panel border-line rounded-panel flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl';
const HEADER = 'border-line bg-panel2 shrink-0 space-y-0.5 border-b px-4 py-3 text-left';
const TITLE = 'text-foreground text-title font-medium';
// relative: a Radix Checkbox or Select in a form renders a hidden absolute input. Positioned against
// the dialog it sat outside this scroller and made the whole dialog scrollable, so moving focus down
// the form slid the header off the top and left a blank band under the footer.
const BODY = 'relative min-h-0 flex-1 overflow-y-auto p-4';
const FOOTER = 'border-line bg-panel2 flex shrink-0 flex-col gap-2 border-t px-4 py-3';
const ACTIONS = 'flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end';
const CANCEL = 'border-input h-9 rounded-lg bg-transparent text-body';
const PRIMARY = 'h-9 rounded-lg text-body';

/**
 * Making a token, in one dialog with two steps. First the form: name, scopes, websites, expiry and
 * the password, with whatever stops it next to the actions. Then the dialog becomes the token,
 * which can never be shown again, so closing it uncopied asks first.
 */
export const CreateTokenDialog = ({ manager }: ManagerProps) => (
  <>
    <Dialog open={manager.isDialogOpen} onOpenChange={(next) => !next && manager.requestClose()}>
      <DialogContent className={SHELL}>
        {manager.secret ? <SecretStep manager={manager} /> : <FormStep manager={manager} />}
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      open={manager.confirmingClose}
      title={COPY.closeTitle}
      confirmLabel={COPY.closeConfirm}
      confirmVariant="destructive"
      onConfirm={manager.confirmClose}
      onCancel={manager.cancelClose}
    >
      <p className="supporting m-0">{COPY.closeBody}</p>
    </ConfirmDialog>
  </>
);

const FormStep = ({ manager }: ManagerProps) => {
  const { blockingReason, isCreating, createError, passkeyNotice } = manager;

  // A form, so Enter in the name or the password creates, as the invite dialog's does. A disabled
  // submit button blocks that in the browser, and create() checks again.
  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    void manager.create();
  };

  return (
    <>
      <DialogHeader className={HEADER}>
        <DialogTitle className={TITLE}>{COPY.createTitle}</DialogTitle>
        <DialogDescription className="supporting m-0">{COPY.createDescription}</DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className={BODY}>
          {manager.isOptionsError ? (
            <LoadFailed text={COPY.optionsFailed} onRetry={manager.retry} />
          ) : (
            <div className="grid gap-4">
              <NameField manager={manager} />
              <ScopesField manager={manager} />
              <WebsitesField manager={manager} />
              <ExpiryField manager={manager} />
              <PasswordField manager={manager} />
            </div>
          )}
        </div>

        <div className={FOOTER}>
          {!!passkeyNotice && <PasskeyNotice text={passkeyNotice} />}
          {!!createError && (
            <p role="alert" className="supporting text-danger m-0">
              {createError}
            </p>
          )}
          <div className={ACTIONS}>
            {/* The notice above, or the failed load in the body, already says why. */}
            {!!blockingReason && !passkeyNotice && !manager.isOptionsError && (
              <span className="supporting sm:mr-auto">{blockingReason}</span>
            )}
            <Button type="button" variant="outline" onClick={manager.requestClose} className={CANCEL}>
              {COPY.cancel}
            </Button>
            <Button type="submit" disabled={!!blockingReason || isCreating} title={blockingReason} className={PRIMARY}>
              {isCreating ? COPY.creating : COPY.create}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

type CopyBlockProps = {
  label: string;
  copyLabel: string;
  copied: boolean;
  onCopy: () => void;
  autoFocus?: boolean;
  children: ReactNode;
};

/** A value to copy, in the block the server keys dialog shows a new key's secret in. */
const CopyBlock = ({ label, copyLabel, copied, onCopy, autoFocus, children }: CopyBlockProps) => (
  <div className="border-sel-line bg-sel-bg rounded-card flex min-w-0 flex-col gap-2 border p-3.5">
    <div className="flex items-center justify-between gap-2">
      <span className="text-foreground text-body font-medium">{label}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCopy}
        aria-label={copyLabel}
        autoFocus={autoFocus}
        className="micro-label text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg px-2"
      >
        {copied ? COPY.copied : COPY.copy}
      </Button>
    </div>
    <pre className="bg-field border-line m-0 overflow-x-auto rounded-lg border p-3">
      <code className="log-line text-foreground whitespace-pre-wrap [overflow-wrap:anywhere]">{children}</code>
    </pre>
  </div>
);

const SecretStep = ({ manager }: ManagerProps) => (
  <>
    <DialogHeader className={HEADER}>
      <DialogTitle className={TITLE}>{COPY.secretTitle}</DialogTitle>
      <DialogDescription className="supporting m-0">{COPY.secretOnce}</DialogDescription>
    </DialogHeader>

    <div className={BODY}>
      <div className="grid gap-4">
        <CopyBlock
          label={COPY.tokenLabel}
          copyLabel={COPY.copyToken}
          copied={manager.copied}
          onCopy={manager.copySecret}
          // The field that had focus is gone with the form, which would leave it on the dialog itself.
          autoFocus
        >
          {manager.secret}
        </CopyBlock>
        <CopyBlock
          label={COPY.commandLabel}
          copyLabel={COPY.copyCommand}
          copied={manager.commandCopied}
          onCopy={manager.copyCommand}
        >
          {manager.mcpCommand}
        </CopyBlock>
      </div>
    </div>

    <div className={FOOTER}>
      <div className={ACTIONS}>
        <Button type="button" onClick={manager.requestClose} className={PRIMARY}>
          {COPY.dismissSecret}
        </Button>
      </div>
    </div>
  </>
);

const NameField = ({ manager }: ManagerProps) => (
  <Field label={COPY.nameLabel} htmlFor="agent-token-name" hint={COPY.nameHelp}>
    <Input
      id="agent-token-name"
      value={manager.name}
      onChange={(ev) => manager.setName(ev.target.value)}
      placeholder={COPY.namePlaceholder}
      maxLength={64}
      className={FIELD_CONTROL}
    />
  </Field>
);

const ScopesField = ({ manager }: ManagerProps) => (
  <fieldset className="m-0 grid gap-2 border-0 p-0">
    <legend className="micro-label mb-1.5">{COPY.scopesLabel}</legend>
    {manager.allowedScopes.map((scope) => (
      <label key={scope} className="flex cursor-pointer items-start gap-2.5">
        <Checkbox
          checked={manager.scopes.includes(scope)}
          onCheckedChange={() => manager.toggleScope(scope)}
          className="mt-0.5"
          aria-describedby={`agent-scope-${scope}`}
        />
        <span className="grid gap-0.5">
          <span className="text-foreground text-body">{SCOPE_COPY[scope].label}</span>
          <span id={`agent-scope-${scope}`} className="supporting">
            {SCOPE_COPY[scope].description}
          </span>
        </span>
      </label>
    ))}
    {manager.readOnly && <p className="supporting m-0">{COPY.memberScopes}</p>}
  </fieldset>
);

const MODES: readonly { value: WebsiteMode; label: string }[] = [
  { value: 'all', label: COPY.allWebsites },
  { value: 'some', label: COPY.someWebsites },
];

/** The websites the caller works on, grouped by account, so an agency or staff user can find one. */
const groupByCompany = (websites: readonly AgentTokenWebsiteOption[]) => {
  // Built up in place: the list can run to thousands, and this runs on every keystroke in the form.
  const groups = new Map<number, { name: string; websites: AgentTokenWebsiteOption[] }>();
  for (const website of websites) {
    const group = groups.get(website.companyId);
    if (group) group.websites.push(website);
    else groups.set(website.companyId, { name: website.companyName, websites: [website] });
  }
  return [...groups.entries()];
};

const WebsitesField = ({ manager }: ManagerProps) => {
  const websites = manager.options?.websites ?? [];
  const groups = groupByCompany(websites);
  const showCompanies = groups.length > 1;
  const selected = new Set(manager.websiteIds);

  return (
    <fieldset className="m-0 grid gap-2 border-0 p-0">
      <legend className="micro-label mb-1.5">{COPY.websitesLabel}</legend>
      {/* A staff token that can make changes must name its sites, so "every website" is replaced by
          why; a read-only staff token keeps the choice. The server refuses either way. */}
      {manager.staffWrite ? (
        <p className="supporting m-0">{COPY.staffAllWebsites}</p>
      ) : (
        <div role="radiogroup" className="flex flex-wrap gap-4">
          {MODES.map((mode) => (
            <label key={mode.value} className="text-foreground flex cursor-pointer items-center gap-2 text-body">
              <input
                type="radio"
                name="agent-token-websites"
                value={mode.value}
                checked={manager.websiteMode === mode.value}
                onChange={() => manager.setWebsiteMode(mode.value)}
                className="accent-primary"
              />
              {mode.label}
            </label>
          ))}
        </div>
      )}
      {manager.websiteMode === 'some' && (
        <div className="border-line bg-panel2 rounded-card border">
          <WebsiteSelectAll
            selected={manager.websiteIds.length}
            total={websites.length}
            onToggle={manager.toggleAllWebsites}
          />
          <div className="grid max-h-56 gap-3 overflow-y-auto p-3">
            {groups.map(([companyId, group]) => (
              <div key={companyId} className="grid gap-1.5">
                {showCompanies && <span className="micro-label">{group.name}</span>}
                {group.websites.map((website) => (
                  <WebsiteRow
                    key={website.id}
                    url={website.url}
                    checked={selected.has(website.id)}
                    onToggle={() => manager.toggleWebsite(website.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </fieldset>
  );
};

const ExpiryField = ({ manager }: ManagerProps) => {
  const choices = manager.options?.expiryDays ?? [];
  return (
    <Field label={COPY.expiryLabel} htmlFor="agent-token-expiry">
      <Select
        name="agent-token-expiry"
        value={String(manager.expiresInDays)}
        options={choices.map((days) => ({ value: days, label: COPY.expiryOption(days) }))}
        onValueChange={(value: string) => manager.setExpiresInDays(Number(value) as AgentTokenExpiryDays)}
        className={cn(FIELD_CONTROL, 'max-w-xs')}
      />
    </Field>
  );
};

const PasswordField = ({ manager }: ManagerProps) => (
  <Field label={COPY.passwordLabel} htmlFor="agent-token-password" hint={COPY.passwordHelp}>
    <Input
      id="agent-token-password"
      type="password"
      autoComplete="current-password"
      value={manager.password}
      onChange={(ev) => manager.setPassword(ev.target.value)}
      className={cn(FIELD_CONTROL, 'max-w-xs')}
    />
  </Field>
);

type PasskeyNoticeProps = { text: string };

/**
 * Shown from the moment the dialog opens when the account's passkey is on another host: passkeys
 * are bound to the host they were registered on, so this site cannot ask for it and Create stays off.
 */
const PasskeyNotice = ({ text }: PasskeyNoticeProps) => (
  <div role="status" className="border-line bg-panel rounded-card flex items-start gap-2.5 border p-3">
    <Info className="text-dim mt-0.5 h-4 w-4 shrink-0" aria-hidden />
    <div className="grid gap-1">
      <p className="text-foreground m-0 text-body">{text}</p>
      <Link to="/settings/account" className="supporting text-foreground underline underline-offset-2">
        {COPY.managePasskeys}
      </Link>
    </div>
  </div>
);
